import { tx } from '../i18n';
import { ApiError } from './api';

/**
 * What a failure means for the reader, rather than what it means for the server.
 *
 * <p>"Something went wrong. Please try again." tells somebody standing in a hallway at a viewing
 * exactly nothing: they cannot tell whether to retype the postcode, wait a minute, or give up and
 * walk in. So every code the API can return is answered here in three parts - what happened, what
 * it means for them, and what they can do about it - and whether pressing "try again" is worth
 * anything at all. The API's own message is kept as the fallback for a code we have not met.
 */
export interface ErrorGuidance {
  title: string;
  detail: string;
  /** False when retrying cannot possibly help, so the button is not offered. */
  retry: boolean;
}

/** Which public source failed, when the server named one. */
function provider(error: ApiError): string | null {
  const named = error.meta.provider;
  return typeof named === 'string' && named.trim() !== '' ? named : null;
}

export function guidanceFor(cause: unknown, fallbackTitle?: string): ErrorGuidance {
  const generic = fallbackTitle
    ?? tx('That did not work', 'Bu işlem olmadı', 'To się nie udało', 'Isso não resultou', 'Eso no ha funcionado');

  if (!(cause instanceof ApiError)) {
    return {
      title: generic,
      detail:
        cause instanceof Error && cause.message
          ? cause.message
          : tx(
              'Please try again in a moment.',
              'Lütfen birazdan tekrar deneyin.',
              'Spróbuj ponownie za chwilę.',
              'Tente novamente daqui a pouco.',
              'Inténtalo de nuevo en un momento.',
            ),
      retry: true,
    };
  }

  switch (cause.code) {
    case 'INVALID_POSTCODE':
      return {
        title: tx(
          'That postcode does not look right',
          'Bu posta kodu doğru görünmüyor',
          'Ten kod pocztowy nie wygląda poprawnie',
          'Esse código postal não parece correto',
          'Ese código postal no parece correcto',
        ),
        detail: tx(
          'A UK postcode looks like E1 6AN or SW1A 1AA. Check it against the listing and enter it again.',
          'Birleşik Krallık posta kodu E1 6AN ya da SW1A 1AA biçimindedir. İlandaki kodla karşılaştırıp yeniden girin.',
          'Brytyjski kod pocztowy wygląda jak E1 6AN albo SW1A 1AA. Porównaj go z ogłoszeniem i wpisz ponownie.',
          'Um código postal britânico é do tipo E1 6AN ou SW1A 1AA. Confirme-o no anúncio e introduza-o de novo.',
          'Un código postal británico es como E1 6AN o SW1A 1AA. Compruébalo en el anuncio y vuelve a escribirlo.',
        ),
        retry: false,
      };

    case 'OUT_OF_SERVICE_AREA':
      return {
        title: tx(
          'We do not cover that address yet',
          'Bu adresi henüz kapsamıyoruz',
          'Nie obsługujemy jeszcze tego adresu',
          'Ainda não cobrimos essa morada',
          'Todavía no cubrimos esa dirección',
        ),
        detail: tx(
          'ScanHome covers England for now. Wales, Scotland and Northern Ireland keep their school and energy records in separate registers, which we have not loaded yet.',
          'ScanHome şimdilik İngiltere’yi kapsıyor. Galler, İskoçya ve Kuzey İrlanda okul ve enerji kayıtlarını ayrı sistemlerde tutuyor; bunları henüz yüklemedik.',
          'ScanHome obejmuje na razie Anglię. Walia, Szkocja i Irlandia Północna prowadzą rejestry szkół i energii osobno, a tych jeszcze nie wczytaliśmy.',
          'O ScanHome cobre Inglaterra por agora. O País de Gales, a Escócia e a Irlanda do Norte mantêm registos de escolas e de energia separados, que ainda não carregámos.',
          'ScanHome cubre Inglaterra por ahora. Gales, Escocia e Irlanda del Norte llevan sus registros de colegios y energía por separado, y aún no los hemos cargado.',
        ),
        retry: false,
      };

    case 'ADDRESS_NOT_FOUND':
      return {
        title: tx(
          'We could not match that address',
          'Bu adres eşleştirilemedi',
          'Nie udało się dopasować tego adresu',
          'Não foi possível encontrar essa morada',
          'No hemos podido localizar esa dirección',
        ),
        detail: tx(
          'Pick an address from the list rather than typing one: the report is about one specific door, so it needs the entry the postcode register holds.',
          'Adresi yazmak yerine listeden seçin: rapor belirli bir konut içindir ve posta kodu kaydındaki kaydı gerektirir.',
          'Wybierz adres z listy zamiast go wpisywać: raport dotyczy konkretnego lokalu, więc potrzebuje wpisu z rejestru kodów pocztowych.',
          'Escolha uma morada da lista em vez de a escrever: o relatório é sobre uma porta concreta, por isso precisa do registo que o registo postal guarda.',
          'Elige una dirección de la lista en lugar de escribirla: el informe es de una vivienda concreta, así que necesita el registro que guarda el registro postal.',
        ),
        retry: false,
      };

    case 'UPSTREAM_UNAVAILABLE': {
      const source = provider(cause);
      return {
        title: source
          ? tx(
              `${source} is not answering`,
              `${source} yanıt vermiyor`,
              `${source} nie odpowiada`,
              `${source} não está a responder`,
              `${source} no responde`,
            )
          : tx(
              'A public data source is not answering',
              'Bir kamu veri kaynağı yanıt vermiyor',
              'Publiczne źródło danych nie odpowiada',
              'Uma fonte de dados públicos não está a responder',
              'Una fuente de datos públicos no responde',
            ),
        detail: tx(
          'This is on their side, not yours. The rest of the report is unaffected; try again in a minute and the missing section should fill in.',
          'Sorun onların tarafında, sizin değil. Raporun geri kalanı etkilenmez; bir dakika sonra tekrar deneyin, eksik bölüm dolacaktır.',
          'To problem po ich stronie, nie po Twojej. Reszta raportu jest nienaruszona; spróbuj za minutę, a brakująca sekcja powinna się uzupełnić.',
          'O problema é do lado deles, não do seu. O resto do relatório não é afetado; tente daqui a um minuto e a secção em falta deverá preencher-se.',
          'El problema es suyo, no tuyo. El resto del informe no se ve afectado; inténtalo dentro de un minuto y la sección que falta debería rellenarse.',
        ),
        retry: true,
      };
    }

    case 'ANALYSIS_TIMEOUT':
    case 'TIMEOUT':
      return {
        title: tx(
          'The data sources took too long',
          'Veri kaynakları çok uzun sürdü',
          'Źródła danych odpowiadały zbyt długo',
          'As fontes de dados demoraram demasiado',
          'Las fuentes de datos han tardado demasiado',
        ),
        detail: tx(
          'We ask several public registers at once and none of them may keep you waiting. Try again — a second attempt usually succeeds, because the slow one is normally slow only briefly.',
          'Birkaç kamu kaydını aynı anda sorguluyoruz ve hiçbiri sizi bekletmemeli. Tekrar deneyin; yavaş olan kaynak genelde kısa süre yavaş olduğu için ikinci deneme çoğunlukla başarılı olur.',
          'Odpytujemy kilka rejestrów publicznych naraz i żaden nie może kazać Ci czekać. Spróbuj ponownie — druga próba zwykle się udaje, bo powolne źródło jest powolne tylko przez chwilę.',
          'Consultamos vários registos públicos ao mesmo tempo e nenhum pode deixá-lo à espera. Tente de novo — a segunda tentativa costuma resultar, porque o registo lento costuma sê-lo por pouco tempo.',
          'Consultamos varios registros públicos a la vez y ninguno puede hacerte esperar. Inténtalo otra vez: el segundo intento suele funcionar, porque el registro lento suele serlo solo un momento.',
        ),
        retry: true,
      };

    case 'RATE_LIMITED':
      return {
        title: tx(
          'Too many requests from this browser',
          'Bu tarayıcıdan çok fazla istek geldi',
          'Zbyt wiele zapytań z tej przeglądarki',
          'Demasiados pedidos deste navegador',
          'Demasiadas peticiones desde este navegador',
        ),
        detail: tx(
          'The limit protects the free public registers we read from. Wait about a minute and carry on where you left off.',
          'Bu sınır, okuduğumuz ücretsiz kamu kayıtlarını korur. Yaklaşık bir dakika bekleyip kaldığınız yerden devam edin.',
          'Limit chroni bezpłatne rejestry publiczne, z których korzystamy. Odczekaj około minuty i wróć do tego, co robiłeś.',
          'O limite protege os registos públicos gratuitos que consultamos. Espere cerca de um minuto e continue onde ficou.',
          'El límite protege los registros públicos gratuitos que consultamos. Espera alrededor de un minuto y sigue donde lo dejaste.',
        ),
        retry: true,
      };

    case 'NETWORK_ERROR':
      return {
        title: tx(
          'We could not reach ScanHome',
          'ScanHome’e ulaşılamadı',
          'Nie udało się połączyć z ScanHome',
          'Não foi possível contactar o ScanHome',
          'No se ha podido contactar con ScanHome',
        ),
        detail: tx(
          'Your device did not get a reply at all, which usually means the connection dropped. Check it and try again; nothing was lost.',
          'Cihazınıza hiç yanıt ulaşmadı; bu genelde bağlantının koptuğu anlamına gelir. Bağlantınızı kontrol edip tekrar deneyin, hiçbir şey kaybolmadı.',
          'Twoje urządzenie nie dostało żadnej odpowiedzi, co zwykle oznacza zerwane połączenie. Sprawdź je i spróbuj ponownie — nic nie zginęło.',
          'O seu dispositivo não recebeu qualquer resposta, o que normalmente significa que a ligação caiu. Verifique-a e tente de novo; não se perdeu nada.',
          'Tu dispositivo no ha recibido respuesta alguna, lo que suele significar que se ha caído la conexión. Compruébala e inténtalo de nuevo; no se ha perdido nada.',
        ),
        retry: true,
      };

    case 'ANALYSIS_NOT_FOUND':
    case 'ANALYSIS_ACCESS_DENIED':
      return {
        title: tx(
          'This report cannot be opened here',
          'Bu rapor burada açılamıyor',
          'Tego raportu nie można tu otworzyć',
          'Este relatório não pode ser aberto aqui',
          'Este informe no se puede abrir aquí',
        ),
        detail: tx(
          'A report is tied to the browser that ran it, so a link opened on another device asks for a key this one does not have. Run the address again — it is free, and takes a few seconds.',
          'Rapor, onu çalıştıran tarayıcıya bağlıdır; başka bir cihazda açılan bağlantı, burada olmayan bir anahtar ister. Adresi yeniden çalıştırın; ücretsizdir ve birkaç saniye sürer.',
          'Raport jest przypisany do przeglądarki, w której powstał, więc link otwarty na innym urządzeniu prosi o klucz, którego tu nie ma. Uruchom adres ponownie — to bezpłatne i zajmuje kilka sekund.',
          'Um relatório está ligado ao navegador que o gerou, por isso um link aberto noutro dispositivo pede uma chave que este não tem. Volte a correr a morada — é gratuito e demora segundos.',
          'Un informe está ligado al navegador que lo generó, así que un enlace abierto en otro dispositivo pide una clave que este no tiene. Vuelve a ejecutar la dirección: es gratis y tarda segundos.',
        ),
        retry: false,
      };

    case 'TOKEN_EXPIRED':
      return {
        title: tx(
          'That report link has expired',
          'Bu rapor bağlantısının süresi doldu',
          'Ten link do raportu wygasł',
          'Esse link do relatório expirou',
          'Ese enlace del informe ha caducado',
        ),
        detail: tx(
          'Unlocked reports stay open for a limited time. Run the address again to get a current one.',
          'Kilidi açılmış raporlar sınırlı bir süre açık kalır. Güncelini almak için adresi yeniden çalıştırın.',
          'Odblokowane raporty pozostają dostępne przez ograniczony czas. Uruchom adres ponownie, aby uzyskać aktualny.',
          'Os relatórios desbloqueados ficam abertos por tempo limitado. Volte a correr a morada para obter um atual.',
          'Los informes desbloqueados permanecen abiertos un tiempo limitado. Vuelve a ejecutar la dirección para obtener uno actual.',
        ),
        retry: false,
      };

    case 'PAYMENT_UNAVAILABLE':
      return {
        title: tx(
          'Payments are not available right now',
          'Ödeme şu anda alınamıyor',
          'Płatności są chwilowo niedostępne',
          'Os pagamentos não estão disponíveis de momento',
          'Los pagos no están disponibles ahora mismo',
        ),
        detail: tx(
          'The free report is unaffected — everything you can see now stays available. Try the unlock again shortly.',
          'Ücretsiz rapor etkilenmez; şu anda gördüğünüz her şey erişilebilir kalır. Kilidi açmayı biraz sonra yeniden deneyin.',
          'Bezpłatny raport pozostaje bez zmian — wszystko, co teraz widzisz, jest nadal dostępne. Spróbuj odblokować za chwilę.',
          'O relatório gratuito não é afetado — tudo o que vê agora continua disponível. Tente desbloquear daqui a pouco.',
          'El informe gratuito no se ve afectado: todo lo que ves ahora sigue disponible. Prueba a desbloquear dentro de un rato.',
        ),
        retry: true,
      };

    default:
      return {
        title: generic,
        detail: cause.message,
        retry: cause.status === 0 || cause.status >= 500,
      };
  }
}
